// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Battle Rhythm Store (Zustand)
// Manages activations, events, and templates across 3 Ignatius tables
// V2 field mapping: extra fields stored as JSON in the notes column
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { createRecord, updateRecord, deleteRecord, TABLES, FIELDS } from '../lib/api-switch';
import { queryRecords } from '../api/client';
import { useAuthStore } from './authStore';

// ── Types ────────────────────────────────────────────────────

export interface Activation {
  id: number;
  incidentId: string;
  opHours: string;       // '12' or '24'
  opStart: string;       // ISO datetime string
  org: string;
  templateId: string;
  status: string;        // 'active' | 'closed'
  closedAt: string;
  creator: string;
  datecreated: string;
}

export interface BREvent {
  id: number;
  incidentId: string;
  title: string;
  category: string;      // briefing | report | shift | external | logistics | planning | other
  scheduledTime: string;  // HH:mm
  durationMinutes: string;
  location: string;
  responsible: string;
  notes: string;
  recurring: string;     // 'true' | 'false'
  adHoc: string;         // 'true' | 'false'
  status: string;        // 'upcoming' | 'in-progress' | 'completed' | 'skipped'
  completedBy: string;
  creator: string;
  datecreated: string;
}

export interface BRTemplate {
  id: number;
  name: string;
  description: string;
  opHours: string;
  events: string;        // JSON string of template event array
  creator: string;
  datecreated: string;
}

export interface TemplateEvent {
  title: string;
  category: string;
  scheduledTime: string;
  durationMinutes: number;
  location: string;
  responsible: string;
  notes: string;
  recurring: boolean;
}

interface BattleRhythmStore {
  activations: Activation[];
  events: BREvent[];
  templates: BRTemplate[];
  loading: boolean;
  error: string | null;

  loadActivations: (incidentId: string) => Promise<void>;
  createActivation: (data: Partial<Activation>) => Promise<number>;
  updateActivation: (rid: number, data: Partial<Activation>) => Promise<void>;
  closeActivation: (rid: number) => Promise<void>;

  loadEvents: (incidentId: string) => Promise<void>;
  createEvent: (data: Partial<BREvent>) => Promise<number>;
  updateEvent: (rid: number, data: Partial<BREvent>) => Promise<void>;
  deleteEvent: (rid: number, incidentId: string) => Promise<void>;

  loadTemplates: () => Promise<void>;
  createTemplate: (data: Partial<BRTemplate>) => Promise<number>;
  updateTemplate: (rid: number, data: Partial<BRTemplate>) => Promise<void>;
  deleteTemplate: (rid: number) => Promise<void>;

  getActivationsForIncident: (incidentId: string) => Activation[];
  getEventsForIncident: (incidentId: string) => BREvent[];
}

// Convert "HH:mm" → full ISO datetime (today's date + given time).
// GAB V2 datetime fields reject bare time strings; normalizeTime strips it back on read.
function timeToISO(t: string): string {
  if (!t) return '';
  const s = t.trim();
  if (s.includes('T')) return s; // already ISO
  const parts = s.split(':');
  const hh = parseInt(parts[0], 10) || 0;
  const mm = parseInt(parts[1], 10) || 0;
  const d = new Date();
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}

// Normalize time: handle "0700", "07:00", "2026-03-30T08:00:00", etc. → "HH:mm"
function normalizeTime(t: string): string {
  if (!t) return '';
  const s = t.trim();
  // ISO datetime: "2026-03-30T08:00:00" → extract "08:00"
  if (s.includes('T')) {
    const match = s.match(/T(\d{1,2}):(\d{2})/);
    if (match) return `${match[1].padStart(2, '0')}:${match[2]}`;
  }
  // Military time without colon: "0700" → "07:00", "900" → "09:00"
  if (/^\d{3,4}$/.test(s)) {
    const padded = s.padStart(4, '0');
    return `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
  }
  // Already "HH:mm" or "HH:mm:ss" — return first two parts
  const parts = s.split(':');
  if (parts.length >= 2) {
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    if (!isNaN(hh) && !isNaN(mm)) return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
  return s;
}

function parseJson(s: string): Record<string, unknown> {
  try { return JSON.parse(s || '{}'); } catch { return {}; }
}

// ── Store ────────────────────────────────────────────────────

export const useBattleRhythmStore = create<BattleRhythmStore>((set, get) => ({
  activations: [],
  events: [],
  templates: [],
  loading: false,
  error: null,

  // ── Activations ──────────────────────────────────────────

  loadActivations: async (incidentId: string) => {
    set({ loading: true, error: null });
    try {
      const FM = FIELDS.brMeta;
      const result = await queryRecords<Record<string, unknown>>(TABLES.brMeta, {
        filters: [{ field: FM.incidentId, operator: 'eq', value: incidentId }],
        limit: 100,
      });
      const activations: Activation[] = (result.records ?? [])
        .filter((row: Record<string, unknown>) => String(row[FM.incidentId] || row.incidentId || '') === incidentId)
        .map((row: Record<string, unknown>) => {
          const extras = parseJson(String(row[FM.notes] || row.notes || '{}'));
          return {
            id: Number(row.record_id || row.id) || 0,
            incidentId: String(row[FM.incidentId] || row.incidentId || ''),
            opHours: String(extras.opHours || row.opHours || '12'),
            opStart: String(extras.opStart || row.opStart || ''),
            org: String(row[FM.name] || extras.org || row.org || ''),
            templateId: String(extras.templateId || row.templateId || ''),
            status: String(row[FM.status] || row.status || 'active'),
            closedAt: String(extras.closedAt || row.closedAt || ''),
            creator: String(extras.creator || row.creator || ''),
            datecreated: String(row.datecreated || ''),
          };
        });
      set({ activations, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load activations' });
    }
  },

  createActivation: async (data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FM = FIELDS.brMeta;
    // Pack extras into notes JSON
    const extras = JSON.stringify({
      opHours: data.opHours || '12',
      opStart: data.opStart || '',
      templateId: data.templateId || '',
      creator: data.creator || '',
    });
    const fields: Array<[string, string]> = [
      [FM.incidentId, data.incidentId || ''],
      [FM.name, data.org || ''],
      [FM.status, data.status || 'active'],
      [FM.notes, extras],
    ];
    const rid = await createRecord(TABLES.brMeta, fields, token);
    if (data.incidentId) await get().loadActivations(data.incidentId);
    return rid;
  },

  updateActivation: async (rid, data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FM = FIELDS.brMeta;
    const existing = get().activations.find(a => a.id === rid);
    // Merge existing extras with new data
    const currentExtras = {
      opHours: existing?.opHours || '12',
      opStart: existing?.opStart || '',
      templateId: existing?.templateId || '',
      creator: existing?.creator || '',
      closedAt: existing?.closedAt || '',
    };
    if (data.opHours !== undefined) currentExtras.opHours = data.opHours;
    if (data.opStart !== undefined) currentExtras.opStart = data.opStart;
    if (data.templateId !== undefined) currentExtras.templateId = data.templateId;
    if (data.creator !== undefined) currentExtras.creator = data.creator;
    if (data.closedAt !== undefined) currentExtras.closedAt = data.closedAt;
    const fields: Array<[string, string]> = [
      [FM.notes, JSON.stringify(currentExtras)],
    ];
    if (data.org !== undefined) fields.push([FM.name, data.org]);
    if (data.status !== undefined) fields.push([FM.status, data.status]);
    await updateRecord(TABLES.brMeta, rid, fields, token);
    if (existing?.incidentId) await get().loadActivations(existing.incidentId);
  },

  closeActivation: async (rid) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FM = FIELDS.brMeta;
    const existing = get().activations.find(a => a.id === rid);
    const extras = {
      opHours: existing?.opHours || '12',
      opStart: existing?.opStart || '',
      templateId: existing?.templateId || '',
      creator: existing?.creator || '',
      closedAt: new Date().toISOString(),
    };
    await updateRecord(TABLES.brMeta, rid, [
      [FM.status, 'closed'],
      [FM.notes, JSON.stringify(extras)],
    ], token);
    if (existing?.incidentId) await get().loadActivations(existing.incidentId);
  },

  // ── Events ───────────────────────────────────────────────

  loadEvents: async (incidentId: string) => {
    try {
      const FE = FIELDS.brEvents;
      // incidentId is stored in notes JSON, so filter client-side
      const result = await queryRecords<Record<string, unknown>>(TABLES.brEvents, { limit: 1000 });
      const events: BREvent[] = (result.records ?? [])
        .filter((row: Record<string, unknown>) => {
          const extras = parseJson(String(row[FE.notes] || row.notes || '{}'));
          const rowIncidentId = String(extras.incidentId || row.incidentId || '');
          return rowIncidentId === incidentId;
        })
        .map((row: Record<string, unknown>) => {
          const extras = parseJson(String(row[FE.notes] || row.notes || '{}'));
          return {
            id: Number(row.record_id || row.id) || 0,
            incidentId: String(extras.incidentId || row.incidentId || incidentId),
            title: String(row[FE.title] || row.title || ''),
            category: String(row[FE.eventType] || extras.category || row.category || 'other'),
            scheduledTime: normalizeTime(String(row[FE.startTime] || extras.scheduledTime || row.scheduledTime || '')),
            durationMinutes: String(extras.durationMinutes || row.durationMinutes || '30'),
            location: String(row[FE.location] || row.location || ''),
            responsible: String(row[FE.participants] || extras.responsible || row.responsible || ''),
            notes: String(extras.userNotes || ''),
            recurring: String(row[FE.recurrence] || extras.recurring || row.recurring || 'false'),
            adHoc: String(extras.adHoc || row.adHoc || 'false'),
            status: String(row[FE.status] || row.status || 'upcoming'),
            completedBy: String(extras.completedBy || row.completedBy || ''),
            creator: String(extras.creator || row.creator || ''),
            datecreated: String(row.datecreated || ''),
            participantPersonIds: FE.participantPersonIds ? (row[FE.participantPersonIds] as string) || '' : '',
          };
        });
      set({ events });
    } catch (err) {
      console.error('Failed to load BR events:', err);
    }
  },

  createEvent: async (data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FE = FIELDS.brEvents;
    // Pack extras into notes JSON
    const extras = JSON.stringify({
      incidentId: data.incidentId || '',
      durationMinutes: data.durationMinutes || '30',
      adHoc: data.adHoc || 'false',
      completedBy: data.completedBy || '',
      creator: data.creator || '',
      userNotes: data.notes || '',
      category: data.category || 'other',
      scheduledTime: data.scheduledTime || '00:00',
      responsible: data.responsible || '',
      recurring: data.recurring || 'false',
    });
    const fields: Array<[string, string]> = [
      [FE.title, data.title || ''],
      [FE.eventType, data.category || 'other'],
      [FE.startTime, timeToISO(data.scheduledTime || '00:00')],
      [FE.location, data.location || ''],
      [FE.participants, data.responsible || ''],
      [FE.recurrence, data.recurring || 'false'],
      [FE.status, data.status || 'upcoming'],
      [FE.notes, extras],
      ...(FE.participantPersonIds && data.participantPersonIds ? [[FE.participantPersonIds, String(data.participantPersonIds)]] as Array<[string, string]> : []),
    ];
    const rid = await createRecord(TABLES.brEvents, fields, token);
    if (data.incidentId) await get().loadEvents(data.incidentId);
    return rid;
  },

  updateEvent: async (rid, data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FE = FIELDS.brEvents;
    const existing = get().events.find(e => e.id === rid);
    const currentExtras = {
      incidentId: existing?.incidentId || data.incidentId || '',
      durationMinutes: existing?.durationMinutes || '30',
      adHoc: existing?.adHoc || 'false',
      completedBy: existing?.completedBy || '',
      creator: existing?.creator || '',
      userNotes: existing?.notes || '',
      category: existing?.category || 'other',
      scheduledTime: existing?.scheduledTime || '00:00',
      responsible: existing?.responsible || '',
      recurring: existing?.recurring || 'false',
    };
    if (data.durationMinutes !== undefined) currentExtras.durationMinutes = data.durationMinutes;
    if (data.adHoc !== undefined) currentExtras.adHoc = data.adHoc;
    if (data.completedBy !== undefined) currentExtras.completedBy = data.completedBy;
    if (data.creator !== undefined) currentExtras.creator = data.creator;
    if (data.notes !== undefined) currentExtras.userNotes = data.notes;
    if (data.category !== undefined) currentExtras.category = data.category;
    if (data.scheduledTime !== undefined) currentExtras.scheduledTime = data.scheduledTime;
    if (data.responsible !== undefined) currentExtras.responsible = data.responsible;
    if (data.recurring !== undefined) currentExtras.recurring = data.recurring;

    const fields: Array<[string, string]> = [
      [FE.notes, JSON.stringify(currentExtras)],
    ];
    if (data.title !== undefined) fields.push([FE.title, data.title]);
    if (data.category !== undefined) fields.push([FE.eventType, data.category]);
    if (data.scheduledTime !== undefined) fields.push([FE.startTime, timeToISO(data.scheduledTime)]);
    if (data.location !== undefined) fields.push([FE.location, data.location]);
    if (data.responsible !== undefined) fields.push([FE.participants, data.responsible]);
    if (data.recurring !== undefined) fields.push([FE.recurrence, data.recurring]);
    if (data.status !== undefined) fields.push([FE.status, data.status]);
    if (fields.length > 1) await updateRecord(TABLES.brEvents, rid, fields, token);
    const incidentId = data.incidentId || existing?.incidentId;
    if (incidentId) await get().loadEvents(incidentId);
  },

  deleteEvent: async (rid, incidentId) => {
    const token = useAuthStore.getState().token ?? undefined;
    await deleteRecord(TABLES.brEvents, rid, token);
    set(state => ({ events: state.events.filter(e => e.id !== rid) }));
    if (incidentId) await get().loadEvents(incidentId);
  },

  // ── Templates ────────────────────────────────────────────

  loadTemplates: async () => {
    try {
      const FT = FIELDS.brTemplates;
      const result = await queryRecords<Record<string, unknown>>(TABLES.brTemplates, { limit: 200 });
      const templates: BRTemplate[] = (result.records ?? []).map((row: Record<string, unknown>) => {
        const extras = parseJson(String(row[FT.description] || row.description || '{}'));
        // description may have been repurposed as extras JSON in some records
        // fall back to plain description string if JSON parse fails on it
        const description = typeof extras === 'object' && extras.desc
          ? String(extras.desc)
          : String(row[FT.description] || row.description || '');
        return {
          id: Number(row.record_id || row.id) || 0,
          name: String(row[FT.name] || row.name || ''),
          description,
          opHours: String(extras.opHours || row.opHours || '12'),
          events: String(row[FT.eventsJson] || extras.events || row.events || '[]'),
          creator: String(extras.creator || row.creator || ''),
          datecreated: String(row.datecreated || ''),
        };
      });
      set({ templates });
    } catch (err) {
      console.error('Failed to load BR templates:', err);
    }
  },

  createTemplate: async (data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FT = FIELDS.brTemplates;
    const fields: Array<[string, string]> = [
      [FT.name, data.name || ''],
      [FT.description, data.description || ''],
      [FT.eventsJson, data.events || '[]'],
    ];
    const rid = await createRecord(TABLES.brTemplates, fields, token);
    await get().loadTemplates();
    return rid;
  },

  updateTemplate: async (rid, data) => {
    const token = useAuthStore.getState().token ?? undefined;
    const FT = FIELDS.brTemplates;
    const fields: Array<[string, string]> = [];
    if (data.name !== undefined) fields.push([FT.name, data.name]);
    if (data.description !== undefined) fields.push([FT.description, data.description]);
    if (data.events !== undefined) fields.push([FT.eventsJson, data.events]);
    if (fields.length > 0) await updateRecord(TABLES.brTemplates, rid, fields, token);
    await get().loadTemplates();
  },

  deleteTemplate: async (rid) => {
    const token = useAuthStore.getState().token ?? undefined;
    await deleteRecord(TABLES.brTemplates, rid, token);
    set(state => ({ templates: state.templates.filter(t => t.id !== rid) }));
  },

  // ── Computed ─────────────────────────────────────────────

  getActivationsForIncident: (incidentId: string) =>
    get().activations.filter(a => String(a.incidentId) === String(incidentId)),

  getEventsForIncident: (incidentId: string) =>
    get().events.filter(e => String(e.incidentId) === String(incidentId)),
}));
